import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * How it works — four stages on a single rule.
 *
 * The stages are genuinely sequential (you cannot collect reviews before
 * assigning reviewers), so this is the one section where a horizontal reading
 * carries meaning rather than just filling width. A single hairline runs
 * through all four, with a marker at each stage: the spine says "these are one
 * process", the markers say "these are four steps".
 *
 * The rule is drawn with `border-t` on a flex row rather than an SVG, so it
 * mirrors under RTL for free — the sequence genuinely should run right-to-left
 * in Arabic, unlike the numerals inside it, which stay LTR.
 *
 * Stacks to a vertical spine below md, where four columns would give each stage
 * about eleven characters per line.
 */
const STEPS = ['setup', 'assign', 'review', 'decide'];

const HowItWorks = () => {
  const { t } = useTranslation('conferences');

  return (
    <section className="bg-background">
      <div className="page-shell px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl mb-12 md:mb-16">
          <h2 className="font-heading font-bold text-foreground text-[26px] sm:text-[32px] leading-[1.15]">
            {t('landing.howItWorks.title')}
          </h2>
          <p className="mt-4 font-academic text-[17px] leading-[1.7] text-muted-foreground">
            {t('landing.howItWorks.subtitle')}
          </p>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-4 gap-y-10 md:gap-y-0 md:gap-x-8">
          {STEPS.map((key, i) => (
            <li key={key} className="relative">
              {/* The spine. On md+ it is the top border of every stage, so the
                  four segments join into one continuous rule. */}
              <div aria-hidden="true" className="hidden md:block h-px bg-border" />
              <div
                aria-hidden="true"
                className="hidden md:block absolute top-0 start-0 -translate-y-1/2 w-2 h-2 bg-mark"
              />
              {/* Vertical spine on small screens. */}
              <div aria-hidden="true" className="md:hidden absolute top-1 bottom-0 start-0 w-px bg-border" />
              <div aria-hidden="true" className="md:hidden absolute top-1 start-0 -translate-x-1/2 rtl:translate-x-1/2 w-2 h-2 bg-mark" />

              <div className="ps-5 md:ps-0 md:pt-6">
                <span className="font-mono text-[13px] tabular-nums text-muted-foreground" dir="ltr">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-2 font-heading font-bold text-foreground text-[19px] leading-[1.3]">
                  {t(`landing.howItWorks.steps.${key}.title`)}
                </h3>
                <p className="mt-2 font-academic text-[16px] leading-[1.7] text-muted-foreground">
                  {t(`landing.howItWorks.steps.${key}.description`)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default HowItWorks;
